/**
 * Hook to get the user's timezone preference from AuthContext.
 * Returns the IANA timezone string (e.g., 'America/Los_Angeles').
 * Falls back to the org's zone (when the caller knows it), then the browser's.
 */
import { useOptionalAuth } from '../contexts/AuthContext';
import { formatDateInTimezone, formatDate, formatRelativeTime } from '../utils/dateTime';
import { resolveTimeZone } from '../utils/zonedTime';
import { getStoredTimezone } from '../lib/memberPreferences';

/**
 * Returns the user's selected timezone from their preferences.
 * Falls back to the browser's local timezone via Intl API.
 *
 * Uses useOptionalAuth so it works in the member portal (no AuthProvider) too —
 * there it simply falls back to the browser timezone.
 */
export function useUserTimezone(orgTimeZone?: string | null): string {
  const auth = useOptionalAuth();
  // TRUS-695: the user's selected zone, else (member portal, no AuthProvider)
  // the member's saved zone, else the org's, else the browser's.
  const own = auth ? auth.preferences?.timezone : getStoredTimezone();
  return resolveTimeZone(own, orgTimeZone);
}

/**
 * Returns timezone-aware formatting functions pre-bound to the user's timezone.
 * This eliminates the need to pass timezone to every formatting call.
 *
 * Usage:
 *   const { fmt } = useFormattedDate();
 *   fmt.dateTime(someISOString)   // "12/31/2023, 2:30 PM"
 *   fmt.short(someISOString)      // "12/31/2023"
 *   fmt.time(someISOString)       // "2:30 PM"
 *   fmt.medium(someISOString)     // "Dec 31, 2023"
 *   fmt.long(someISOString)       // "December 31, 2023"
 *   fmt.full(someISOString)       // "Sunday, December 31, 2023"
 *   fmt.custom(someISOString, 'MMM d, h:mm a')  // custom format string
 */
export function useFormattedDate() {
  const timezone = useUserTimezone();
  const auth = useOptionalAuth();
  const lang = auth?.preferences?.language_code || 'en';

  return {
    timezone,
    fmt: {
      short: (date: string | Date) => formatDate.short(date, timezone, lang),
      medium: (date: string | Date) => formatDate.medium(date, timezone, lang),
      long: (date: string | Date) => formatDate.long(date, timezone, lang),
      full: (date: string | Date) => formatDate.full(date, timezone, lang),
      time: (date: string | Date) => formatDate.time(date, timezone, lang),
      timeWithSeconds: (date: string | Date) => formatDate.timeWithSeconds(date, timezone, lang),
      dateTime: (date: string | Date) => formatDate.dateTime(date, timezone, lang),
      dateTimeFull: (date: string | Date) => formatDate.dateTimeFull(date, timezone, lang),
      relative: (date: string | Date) => formatRelativeTime(date, timezone, lang),
      custom: (date: string | Date, formatString: string) =>
        formatDateInTimezone(date, timezone, formatString, lang),
    },
  };
}
